import type {
  ClassOperationItem,
  ProblemTestCase,
  ProblemTestCaseInput,
} from './types'

// =============================================================================
// Constants
// =============================================================================

export const MAX_TEST_CASE_TABS = 8

// =============================================================================
// JSON Parsing Utilities
// =============================================================================

/**
 * Safely parse a stored JSON value (frontmatter or DB test-case data).
 * These values should always be valid JSON (strings quoted, etc.); on a parse
 * failure we log and fall back to the raw string rather than swallowing it.
 */
export function parseJsonValue(value: string | undefined | null): unknown {
  if (value === undefined || value === null) return undefined
  try {
    return JSON.parse(value)
  } catch {
    // Log warning for debugging - stored values should be valid JSON
    console.warn(`Invalid JSON value: "${value}". Returning as raw string.`)
    return value
  }
}

/**
 * Parse expected value from user input.
 * Uses strict JSON parsing to ensure type consistency.
 *
 * IMPORTANT: All expected values must be valid JSON:
 * - Strings: "hello" (with quotes) or "42" (string forty-two)
 * - Numbers: 42 (without quotes)
 * - Booleans: true/false
 * - Null: null
 * - Arrays: [1,2,3]
 * - Objects: {"key": "value"}
 *
 * This ensures lossless round-trips when saving to the database.
 */
export function parseExpectedValue(value: string): unknown {
  const trimmed = value.trim()
  if (!trimmed.length) {
    return ''
  }
  try {
    return JSON.parse(trimmed)
  } catch {
    // Log warning - user should enter valid JSON
    console.warn(
      `Expected value "${value}" is not valid JSON. ` +
        `If this should be a string, wrap it in quotes: "${JSON.stringify(value)}"`,
    )
    return value
  }
}

/**
 * Stringify a value for display/editing.
 * Always uses JSON.stringify to ensure values can be round-tripped.
 *
 * - String "hello" → "\"hello\""
 * - Number 42 → "42"
 * - null → "null"
 */
export function stringifyValue(value: unknown): string {
  if (value === undefined) {
    return 'undefined'
  }
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/**
 * Get default input value as string
 */
export function getDefaultInputValue(value: unknown): string {
  return stringifyValue(value)
}

// =============================================================================
// State Map Utilities
// =============================================================================

/**
 * Create input value map from test cases
 */
export function createInputValueMap(
  cases: ProblemTestCase[],
): Map<string, Map<string, string>> {
  const inputsMap = new Map<string, Map<string, string>>()
  cases.forEach((testCase) => {
    inputsMap.set(testCase._id, buildCaseInputMap(testCase.inputs))
  })
  return inputsMap
}

/**
 * Create expected value map from test cases
 */
export function createExpectedValueMap(
  cases: ProblemTestCase[],
): Map<string, string> {
  const map = new Map<string, string>()
  cases.forEach((testCase) => {
    map.set(testCase._id, stringifyValue(testCase.expected))
  })
  return map
}

/**
 * Build input map for a single test case
 */
export function buildCaseInputMap(
  inputs: ProblemTestCaseInput[],
  existingValues?: Map<string, string> | null,
): Map<string, string> {
  const caseInputs = new Map<string, string>()
  inputs.forEach((input) => {
    const preservedValue = existingValues?.get(input.name)
    caseInputs.set(
      input.name,
      preservedValue ?? getDefaultInputValue(input.example),
    )
  })
  return caseInputs
}

/**
 * Clone a test case with a new ID
 */
export function cloneTestCase(
  template: ProblemTestCase | null,
  newId: string,
): ProblemTestCase {
  return {
    _id: newId,
    id: undefined,
    testCaseIntent: template?.testCaseIntent ?? 'Custom test case',
    expected: '',
    args: template?.args ? [...template.args] : [],
    inputs: (template?.inputs ?? []).map((input) => ({
      name: input.name,
      example: input.example,
    })),
  }
}

// =============================================================================
// Operation Formatting
// =============================================================================

/**
 * Format an operation as method call and expected parts separately
 * Returns { call: "obj.method(args)", expected: "value" }
 * Note: Only works with operation items, not comments.
 */
export function formatOperationParts(operation: ClassOperationItem): {
  call: string
  expected: string
} {
  const method = operation.method || 'method'

  // Parse args JSON array and format as function arguments
  let argsStr = ''
  try {
    const argsArray = JSON.parse(operation.args || '[]')
    if (Array.isArray(argsArray)) {
      argsStr = argsArray.map((arg) => JSON.stringify(arg)).join(', ')
    } else {
      argsStr = operation.args
    }
  } catch {
    // If not valid JSON, use raw string (strip brackets if present)
    argsStr = operation.args.replace(/^\[|\]$/g, '')
  }

  // Format expected value
  let expectedStr: string
  if (operation.expectsError) {
    expectedStr = 'raises Error'
  } else {
    expectedStr = operation.expected
    try {
      const parsed = JSON.parse(operation.expected)
      expectedStr = JSON.stringify(parsed)
    } catch {
      // Use as-is if not valid JSON
    }
  }

  return {
    call: `obj.${method}(${argsStr})`,
    expected: expectedStr,
  }
}

/**
 * Format an operation as a code preview string (legacy, used for single line)
 * e.g., "obj.create_grant(0, "x", 10, 50) # null"
 * Note: Only works with operation items, not comments.
 */
export function formatOperationPreview(operation: ClassOperationItem): string {
  const { call, expected } = formatOperationParts(operation)
  return `${call} # ${expected}`
}
